import React, { useState, useEffect } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import {
  Box,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Paper,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EventIcon from "@mui/icons-material/Event";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import TitleChnage from "../../Hook/TitleChnage";

const specialDates = {
  "2025-01-05": "purple",
  "2024-07-13": "half-yellow-purple",
  "2024-07-20": "yellow",
  "2024-07-30": "small-yellow",
  "2024-07-31": "small-yellow",
  "2024-08-01": "gold",
  "2024-08-02": "small-yellow",
  "2024-08-04": "purple",
};

// Styled components for modern look
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "20px",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },
}));

const GradientCard = styled(Card)(({ theme }) => ({
  borderRadius: "20px",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: "white",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
  },
}));

const Dashboard = () => {
  const theme = useTheme();
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [userType, setUserType] = useState("admin"); // Can be "admin", "manager", "user"
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Simulate getting user type from authentication
    // In real app, this would come from your auth context/state
    const getUserType = async () => {
      // Replace with actual user type fetching logic
      setUserType("admin");
    };
    getUserType();

    // Update current time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  TitleChnage("Dashboard");

  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, "month"));
  };

  // Mock data for different user types
  const adminData = {
    stats: [
      {
        title: "Total Users",
        value: "2,543",
        change: "+12%",
        icon: <PeopleIcon sx={{ fontSize: 40 }} />,
        color: "#667eea",
      },
      {
        title: "Revenue",
        value: "$45,231",
        change: "+23%",
        icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
        color: "#f59e0b",
      },
      {
        title: "Active Projects",
        value: "24",
        change: "+3",
        icon: <WorkIcon sx={{ fontSize: 40 }} />,
        color: "#10b981",
      },
      {
        title: "Completion Rate",
        value: "94%",
        change: "+5%",
        icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
        color: "#ef4444",
      },
    ],
    recentActivities: [
      { user: "John Doe", action: "Completed project", time: "2 hours ago", avatar: "JD" },
      { user: "Jane Smith", action: "Submitted report", time: "3 hours ago", avatar: "JS" },
      { user: "Mike Johnson", action: "Started new task", time: "5 hours ago", avatar: "MJ" },
      { user: "Sarah Wilson", action: "Updated profile", time: "1 day ago", avatar: "SW" },
    ],
    upcomingEvents: [
      { title: "Team Meeting", date: "2024-01-20", time: "10:00 AM", attendees: 12 },
      { title: "Project Review", date: "2024-01-22", time: "2:00 PM", attendees: 8 },
      { title: "Client Call", date: "2024-01-25", time: "11:30 AM", attendees: 5 },
    ],
  };

  const managerData = {
    stats: [
      {
        title: "Team Members",
        value: "12",
        change: "+2",
        icon: <PeopleIcon sx={{ fontSize: 40 }} />,
        color: "#667eea",
      },
      {
        title: "Tasks Completed",
        value: "156",
        change: "+28",
        icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
        color: "#10b981",
      },
      {
        title: "Pending Reviews",
        value: "8",
        change: "-3",
        icon: <EventIcon sx={{ fontSize: 40 }} />,
        color: "#f59e0b",
      },
      {
        title: "Team Performance",
        value: "92%",
        change: "+4%",
        icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
        color: "#ef4444",
      },
    ],
    recentActivities: [
      { user: "Team Alpha", action: "Sprint completed", time: "1 hour ago", avatar: "TA" },
      { user: "Team Beta", action: "Code review needed", time: "3 hours ago", avatar: "TB" },
      { user: "Design Team", action: "UI mockups ready", time: "5 hours ago", avatar: "DT" },
    ],
    upcomingEvents: [
      { title: "Sprint Planning", date: "2024-01-21", time: "9:30 AM", attendees: 8 },
      { title: "Team Standup", date: "2024-01-22", time: "10:00 AM", attendees: 12 },
    ],
  };

  const userData = {
    stats: [
      {
        title: "My Tasks",
        value: "8",
        change: "+2",
        icon: <WorkIcon sx={{ fontSize: 40 }} />,
        color: "#667eea",
      },
      {
        title: "Completed",
        value: "24",
        change: "+5",
        icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
        color: "#10b981",
      },
      {
        title: "Hours Logged",
        value: "128",
        change: "+12",
        icon: <EventIcon sx={{ fontSize: 40 }} />,
        color: "#f59e0b",
      },
      {
        title: "Productivity",
        value: "87%",
        change: "+3%",
        icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
        color: "#ef4444",
      },
    ],
    recentActivities: [
      { user: "You", action: "Completed task: UI Design", time: "1 hour ago", avatar: "ME" },
      { user: "You", action: "Submitted timesheet", time: "3 hours ago", avatar: "ME" },
      { user: "You", action: "Started new task", time: "5 hours ago", avatar: "ME" },
    ],
    upcomingEvents: [
      { title: "Project Meeting", date: "2024-01-20", time: "2:00 PM", attendees: 6 },
      { title: "Code Review", date: "2024-01-23", time: "11:00 AM", attendees: 3 },
    ],
  };

  const currentData = userType === "admin" ? adminData : userType === "manager" ? managerData : userData;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            Welcome back, {userType === "admin" ? "Admin" : userType === "manager" ? "Manager" : "User"}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your {userType === "admin" ? "platform" : userType === "manager" ? "team" : "tasks"} today.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {currentTime.toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Stats Cards */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {currentData.stats.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <StyledCard>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Box
                          sx={{
                            bgcolor: alpha(stat.color, 0.1),
                            borderRadius: "12px",
                            p: 1,
                            display: "inline-flex",
                          }}
                        >
                          {React.cloneElement(stat.icon, { sx: { color: stat.color, fontSize: 32 } })}
                        </Box>
                        <Chip
                          label={stat.change}
                          size="small"
                          sx={{
                            bgcolor: stat.change.startsWith("+") ? alpha("#10b981", 0.1) : alpha("#ef4444", 0.1),
                            color: stat.change.startsWith("+") ? "#10b981" : "#ef4444",
                            fontWeight: "bold",
                          }}
                        />
                      </Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.title}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={8}>
            {/* Recent Activity Section */}
            <StyledCard sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Recent Activity
                </Typography>
                <List>
                  {currentData.recentActivities.map((activity, index) => (
                    <React.Fragment key={index}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {activity.avatar}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight="bold">
                              {activity.user}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {activity.action}
                            </Typography>
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {activity.time}
                        </Typography>
                      </ListItem>
                      {index < currentData.recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </StyledCard>

            {/* Progress Section */}
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Overall Progress
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">Overall Completion</Typography>
                    <Typography variant="body2" fontWeight="bold">78%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={78} sx={{ height: 8, borderRadius: 4 }} />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">Tasks in Progress</Typography>
                    <Typography variant="body2" fontWeight="bold">45%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={45} sx={{ height: 8, borderRadius: 4 }} />
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Right Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Calendar Section */}
            <StyledCard sx={{ mb: 3, overflow: "visible" }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Calendar
                  </Typography>
                  <Box>
                    <IconButton size="small" onClick={handlePrevMonth}>
                      <ArrowBackIosIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" component="span" sx={{ mx: 1 }}>
                      {currentMonth.format("MMM YYYY")}
                    </Typography>
                    <IconButton size="small" onClick={handleNextMonth}>
                      <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Box
                  sx={{
                    "& .MuiPickersDay-root": {
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      margin: "2px",
                      padding: "6px",
                      width: "36px",
                      height: "36px",
                      fontSize: "0.875rem",
                    },
                    "& .Mui-selected": {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: "white !important",
                    },
                    "& .MuiPickersDay-root:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                    "& .MuiDayCalendar-weekDayLabel": {
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      color: theme.palette.text.secondary,
                    },
                    "& .MuiPickersCalendarHeader-root": {
                      display: "none",
                    },
                  }}
                >
                  <DateCalendar
                    value={currentMonth}
                    onChange={(newDate) => setCurrentMonth(newDate)}
                    views={["day"]}
                    disableHighlightToday
                    showDaysOutsideCurrentMonth
                    sx={{ width: "100%", maxHeight: "320px" }}
                  />
                </Box>
              </CardContent>
            </StyledCard>

            {/* Upcoming Events */}
            <GradientCard>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "white" }}>
                  Upcoming Events
                </Typography>
                {currentData.upcomingEvents.map((event, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <EventIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.9)" }} />
                      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
                        {event.title}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", display: "block", mb: 0.5 }}>
                      {dayjs(event.date).format("MMM DD, YYYY")} at {event.time}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                      {event.attendees} attendees
                    </Typography>
                    {index < currentData.upcomingEvents.length - 1 && (
                      <Divider sx={{ my: 1.5, bgcolor: "rgba(255,255,255,0.2)" }} />
                    )}
                  </Box>
                ))}
              </CardContent>
            </GradientCard>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default Dashboard;